import { MEDIA_UPLOAD_SCOPE_DOMAINS } from "@/lib/media/paths";

const MEDIA_KEY_EXTENSIONS = new Set([
  "avif",
  "webp",
  "png",
  "gif",
  "heic",
  "heif",
  "bmp",
  "tiff",
  "tif",
  "jpg",
  "jpeg",
  "mp4",
  "webm",
  "mov",
  "3gp",
  "3gpp",
  "m4v",
  "m3u8",
  "ts",
  "aac",
  "m4a",
  "mp3",
  "ogg",
  "wav",
]);

const MEDIA_KEY_DOMAIN_SET = new Set<string>(MEDIA_UPLOAD_SCOPE_DOMAINS);
const SAFE_PATH_SEGMENT_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,159}$/;
const SAFE_FILE_NAME_PATTERN = /^[^/\\:]+$/;

const hasTraversalSegment = (value: string) =>
  value
    .replace(/\\/g, "/")
    .split("/")
    .some((segment) => segment === "." || segment === "..");

const hasControlCharacter = (value: string) =>
  [...value].some((char) => {
    const code = char.charCodeAt(0);
    return code === 0 || code < 32 || code === 127;
  });

export function isSafeUploadScopeSegment(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= 160 &&
    !hasTraversalSegment(trimmed) &&
    !trimmed.includes("/") &&
    !trimmed.includes("\\") &&
    !hasControlCharacter(trimmed) &&
    SAFE_PATH_SEGMENT_PATTERN.test(trimmed)
  );
}

export function isSafeUploadFileName(value: string): boolean {
  const trimmed = value.trim();
  if (
    trimmed.length === 0 ||
    trimmed.length > 255 ||
    hasTraversalSegment(trimmed) ||
    hasControlCharacter(trimmed) ||
    !SAFE_FILE_NAME_PATTERN.test(trimmed)
  ) {
    return false;
  }

  const extension = trimmed.split(".").pop()?.toLowerCase();
  return !!extension && MEDIA_KEY_EXTENSIONS.has(extension);
}

export function isSafeMediaKey(value: string): boolean {
  const trimmed = value.trim();
  if (
    trimmed.length === 0 ||
    trimmed.length > 512 ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("//") ||
    /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ||
    trimmed.includes("\\") ||
    hasControlCharacter(trimmed) ||
    hasTraversalSegment(trimmed)
  ) {
    return false;
  }

  const parts = trimmed.split("/");
  if (parts.length !== 4 || !MEDIA_KEY_DOMAIN_SET.has(parts[0])) {
    return false;
  }

  const [domain, entityRef, slot, fileName] = parts;
  if (
    !domain ||
    !isSafeUploadScopeSegment(entityRef) ||
    !isSafeUploadScopeSegment(slot)
  ) {
    return false;
  }

  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
    return false;
  }

  const assetId = fileName.slice(0, dotIndex);
  const extension = fileName.slice(dotIndex + 1).toLowerCase();
  return (
    /^[a-f0-9]{12,64}$/i.test(assetId) && MEDIA_KEY_EXTENSIONS.has(extension)
  );
}

export function isSafeHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}
